// メインSCSSをバンドルに含める
import "../scss/style.scss";
// Keen Slider 本体
import KeenSlider from "keen-slider";

// DOM取得とイベント登録の共通ユーティリティ
const qs = (selector, scope = document) => scope.querySelector(selector);
const qsa = (selector, scope = document) => Array.from(scope.querySelectorAll(selector));
const on = (element, type, handler, options) => {
  if (element) {
    element.addEventListener(type, handler, options);
  }
};
const onAll = (elements, type, handler, options) => {
  elements.forEach((element) => element.addEventListener(type, handler, options));
};

// IFrame Player API の読込をページ内で一度だけにする
let youtubeIframeApiPromise;
const loadYouTubeIframeApi = () => {
  if (window.YT && window.YT.Player) {
    return Promise.resolve(window.YT);
  }

  if (youtubeIframeApiPromise) {
    return youtubeIframeApiPromise;
  }

  youtubeIframeApiPromise = new Promise((resolve, reject) => {
    const previousReady = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      if (typeof previousReady === "function") {
        previousReady();
      }
      resolve(window.YT);
    };

    const script = document.createElement("script");
    script.src = "https://www.youtube.com/iframe_api";
    script.async = true;
    script.onerror = () => reject(new Error("YouTube IFrame Player API の読み込みに失敗しました。"));
    document.head.appendChild(script);
  });

  return youtubeIframeApiPromise;
};

// 動画の終了ごとに、指定された動画を順番に切り替える
const initYouTubeHero = () => {
  const container = qs("[data-youtube-hero]");
  const videoIds = container
    ? (container.dataset.youtubeVideoIds || "")
        .split(",")
        .map((videoId) => videoId.trim())
        .filter(Boolean)
    : [];
  if (!container || !videoIds.length) return;

  let currentVideoIndex = 0;

  loadYouTubeIframeApi()
    .then(() => {
      new window.YT.Player(container, {
        width: 560,
        height: 315,
        videoId: videoIds[currentVideoIndex],
        playerVars: {
          autoplay: 1,
          controls: 0,
          disablekb: 1,
          fs: 0,
          iv_load_policy: 3,
          playsinline: 1,
          rel: 0,
          origin: window.location.origin
        },
        events: {
          onReady: (event) => {
            event.target.mute();
            event.target.playVideo();
          },
          onStateChange: (event) => {
            if (event.data === window.YT.PlayerState.ENDED) {
              currentVideoIndex = (currentVideoIndex + 1) % videoIds.length;
              event.target.loadVideoById(videoIds[currentVideoIndex], 0);
            }
          }
        }
      });
    })
    .catch(() => {
      container.hidden = true;
    });
};

// クロスフェード用の切替制御
const fade = (slider) => {
  const setOpacity = () => {
    slider.slides.forEach((slide, idx) => {
      slide.style.opacity = idx === slider.track.details.rel ? "1" : "0";
    });
  };

  slider.on("created", () => {
    slider.slides.forEach((slide, idx) => {
      slide.style.position = "absolute";
      slide.style.inset = "0";
      slide.style.transition = "opacity 0.8s ease";
      slide.style.opacity = idx === slider.track.details.rel ? "1" : "0";
      slide.style.zIndex = `${slider.slides.length - idx}`;
    });
  });

  slider.on("detailsChanged", setOpacity);
};

// data-keen-hero-src でスライド画像を遅延ロード
const lazyLoad = (slider) => {
  const loadImages = (idx) => {
    const slide = slider.slides[idx];
    if (!slide) return;
    slide.querySelectorAll("img[data-keen-hero-src]").forEach((img) => {
      img.src = img.dataset.keenHeroSrc;
      img.removeAttribute("data-keen-hero-src");
    });
  };

  slider.on("created", () => {
    const { rel } = slider.track.details;
    loadImages(rel);
    loadImages(rel + 1);
  });

  slider.on("detailsChanged", () => {
    const { rel } = slider.track.details;
    loadImages(rel);
    loadImages(rel + 1);
  });
};

// オートプレイ
const autoplay = (delay = 3500) => (slider) => {
  let timeout;
  let isPaused = false;

  const clear = () => {
    if (timeout) {
      clearTimeout(timeout);
    }
  };

  const next = () => {
    clear();
    if (isPaused) return;
    timeout = setTimeout(() => {
      slider.next();
    }, delay);
  };

  slider.on("created", () => {
    next();
  });

  slider.on("dragStarted", clear);
  slider.on("animationEnded", next);
  slider.on("updated", next);
  slider.on("destroyed", clear);

  const api = {
    pause() {
      isPaused = true;
      clear();
    },
    play() {
      isPaused = false;
      next();
    }
  };

  slider.autoplayApi = api;
  return api;
};

// ドット/前後ボタン/再生停止の制御
const navigation = (slider) => {
  const root = slider.container.closest("[data-hero]");
  if (!root) return;
  const dotsWrapper = root.querySelector("[data-keen-hero-dots]");
  const prev = root.querySelector("[data-keen-hero-prev]");
  const next = root.querySelector("[data-keen-hero-next]");
  const stopButton = root.querySelector("[data-keen-hero-stop]");
  const dots = [];

  const setActive = (idx) => {
    dots.forEach((dot, dotIdx) => {
      dot.classList.toggle("is-active", dotIdx === idx);
      dot.setAttribute("aria-pressed", dotIdx === idx ? "true" : "false");
    });
  };

  if (dotsWrapper) {
    slider.slides.forEach((_, idx) => {
      const button = document.createElement("button");
      button.type = "button";
      button.className = "c-hero__dot";
      button.setAttribute("data-keen-hero-dot", "");
      button.setAttribute("aria-label", `第${idx + 1}張`);
      button.setAttribute("aria-pressed", "false");
      button.addEventListener("click", () => {
        slider.moveToIdx(idx);
      });
      dotsWrapper.appendChild(button);
      dots.push(button);
    });
  }

  on(prev, "click", () => slider.prev());
  on(next, "click", () => slider.next());

  slider.on("created", () => setActive(slider.track.details.rel));
  slider.on("detailsChanged", () => setActive(slider.track.details.rel));

  const autoplayApi = slider.autoplayApi;
  if (stopButton && autoplayApi) {
    stopButton.addEventListener("click", () => {
      autoplayApi.pause();
      stopButton.disabled = true;
    });
  }
};

// ヒーロースライダー初期化
const initHeroSlider = () => {
  const heroSlider = qs("[data-keen-hero]");
  if (!heroSlider) return;
  new KeenSlider(
    heroSlider,
    {
      loop: true,
      drag: true,
      renderMode: "custom"
    },
    [fade, lazyLoad, autoplay(4000), navigation]
  );
};

// クリックイベントの多重登録を防ぐ
const resetClickTarget = (element) => {
  if (!element) return null;
  if (element._clickResetController) {
    element._clickResetController.abort();
  }
  const controller = new AbortController();
  element._clickResetController = controller;
  return { element, signal: controller.signal };
};

// data属性に紐づくカルーセルスライドを取得
const getCarouselSlides = (sliderEl, dataAttr) => {
  return qsa(`[${dataAttr}-slide]`, sliderEl);
};

// カルーセル用のドットを生成
const createCarouselDots = (slider, dotsWrapper, dataAttr) => {
  if (!dotsWrapper) return [];
  dotsWrapper.innerHTML = "";
  const dots = [];
  slider.slides.forEach((_, idx) => {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "c-carousel__dot";
    button.setAttribute(dataAttr, "");
    button.setAttribute("aria-label", `第${idx + 1}張`);
    button.addEventListener("click", () => {
      slider.moveToIdx(idx);
    });
    dotsWrapper.appendChild(button);
    dots.push(button);
  });
  return dots;
};

// カルーセルスライダー（中央寄せ）
const initCarouselCentered = () => {
  const sliders = qsa("[data-keen-carouselCentered]");
  if (!sliders.length) return;

  sliders.forEach((sliderEl) => {
    const root = sliderEl.closest("[data-carousel-centered]");
    if (!root) return;

    const carouselCenteredNav = (slider) => {
      const prev = resetClickTarget(root.querySelector("[data-keen-carouselCentered-prev]"));
      const next = resetClickTarget(root.querySelector("[data-keen-carouselCentered-next]"));
      const dotsWrapper = root.querySelector("[data-keen-carouselCentered-dots]");
      const dots = createCarouselDots(slider, dotsWrapper, "data-keen-carouselCentered-dot");

      const setActive = (idx) => {
        dots.forEach((dot, dotIdx) => {
          dot.classList.toggle("is-active", dotIdx === idx);
        });
        slider.slides.forEach((slide, slideIdx) => {
          slide.classList.toggle("is-active", slideIdx === idx);
        });
      };

      if (prev) {
        prev.element.addEventListener(
          "click",
          () => {
            const details = slider.track.details;
            if (!details) {
              slider.prev();
              return;
            }
            slider.moveToIdx(details.abs - 1, true);
          },
          { signal: prev.signal }
        );
      }
      if (next) {
        next.element.addEventListener(
          "click",
          () => {
            const details = slider.track.details;
            if (!details) {
              slider.next();
              return;
            }
            slider.moveToIdx(details.abs + 1, true);
          },
          { signal: next.signal }
        );
      }

      slider.on("created", () => {
        setActive(slider.track.details.rel);
      });
      slider.on("detailsChanged", () => {
        setActive(slider.track.details.rel);
      });
    };

    const slideCount = getCarouselSlides(sliderEl, "data-keen-carouselCentered").length;

    if (sliderEl._keenCenteredInstance) {
      sliderEl._keenCenteredInstance.destroy();
    }

    sliderEl._keenCenteredInstance = new KeenSlider(
      sliderEl,
      {
        loop: slideCount > 2,
        drag: true,
        slides: {
          perView: "auto",
          spacing: 16,
          origin: "center"
        }
      },
      [carouselCenteredNav]
    );
  });
};

// カルーセルスライダー（左寄せ）
const initCarousel = () => {
  const sliders = qsa("[data-keen-carousel]");
  if (!sliders.length) return;

  sliders.forEach((sliderEl) => {
    const root = sliderEl.closest("[data-carousel]");
    if (!root) return;

    const baseSlides = getCarouselSlides(sliderEl, "data-keen-carousel");
    const baseCount = baseSlides.length;

    if (baseCount <= 1) {
      if (sliderEl._keenInstance) {
        sliderEl._keenInstance.destroy();
        sliderEl._keenInstance = null;
      }
      baseSlides.forEach((slide) => {
        slide.classList.add("is-active");
      });
      return;
    }

    if (!sliderEl.dataset.carouselCloned) {
      if (baseCount > 0 && baseCount < 3) {
        const targetCount = baseCount === 1 ? 3 : baseCount * 2;
        for (let i = 0; baseCount + i < targetCount; i += 1) {
          const clone = baseSlides[i % baseCount].cloneNode(true);
          sliderEl.appendChild(clone);
        }
      }
      sliderEl.dataset.carouselCloned = "true";
    }

    const carouselNav = (slider) => {
      const prev = resetClickTarget(root.querySelector("[data-keen-carousel-prev]"));
      const next = resetClickTarget(root.querySelector("[data-keen-carousel-next]"));
      const dotsWrapper = root.querySelector("[data-keen-carousel-dots]");
      const dots = createCarouselDots(slider, dotsWrapper, "data-keen-carousel-dot");

      const setActive = (idx) => {
        dots.forEach((dot, dotIdx) => {
          dot.classList.toggle("is-active", dotIdx === idx);
        });
        slider.slides.forEach((slide, slideIdx) => {
          slide.classList.toggle("is-active", slideIdx === idx);
        });
      };

      if (prev) {
        prev.element.addEventListener("click", () => slider.prev(), { signal: prev.signal });
      }
      if (next) {
        next.element.addEventListener("click", () => slider.next(), { signal: next.signal });
      }

      slider.on("created", () => {
        setActive(slider.track.details.rel);
      });
      slider.on("detailsChanged", () => {
        setActive(slider.track.details.rel);
      });
    };

    if (sliderEl._keenInstance) {
      sliderEl._keenInstance.destroy();
    }

    sliderEl._keenInstance = new KeenSlider(
      sliderEl,
      {
        loop: true,
        drag: true,
        slides: {
          perView: "auto",
          spacing: 16,
          origin: "auto"
        }
      },
      [carouselNav]
    );
  });
};

// ヘッダーナビ用カルーセル（中央寄せ）
const initHeaderNavCarouselCentered = () => {
  const sliders = qsa("[data-keen-nav-centered]");
  if (!sliders.length) return;

  sliders.forEach((sliderEl) => {
    if (sliderEl._keenNavInstance) {
      sliderEl._keenNavInstance.destroy();
    }
    sliderEl._keenNavInstance = new KeenSlider(sliderEl, {
      loop: true,
      drag: true,
      mode: "free",
      renderMode: "precision",
      slides: {
        perView: "auto",
        spacing: 16,
        origin: "center"
      }
    });
  });
};

// ヘッダーのスクロール状態を切り替え
const initHeaderScroll = () => {
  const navRoot = qs("[data-nav-root]");
  if (!navRoot) return;
  const resolveThreshold = () => {
    const rect = navRoot.getBoundingClientRect();
    if (rect.height > 0) {
      return rect.height;
    }
    const rootStyle = getComputedStyle(document.documentElement);
    const thresholdRaw = rootStyle.getPropertyValue("--global-header-height").trim();
    if (!thresholdRaw) return 0;
    if (!document.body) return 0;
    const probe = document.createElement("div");
    probe.style.position = "absolute";
    probe.style.visibility = "hidden";
    probe.style.height = thresholdRaw;
    document.body.appendChild(probe);
    const height = probe.getBoundingClientRect().height;
    probe.remove();
    return height;
  };

  let cachedThreshold = resolveThreshold();

  const update = () => {
    if (!Number.isFinite(cachedThreshold) || cachedThreshold <= 0) {
      navRoot.classList.remove("is-scrolled");
      return;
    }
    navRoot.classList.toggle("is-scrolled", window.scrollY > cachedThreshold);
  };

  const updateThreshold = () => {
    cachedThreshold = resolveThreshold();
    update();
  };

  update();
  window.addEventListener("scroll", update, { passive: true });
  window.addEventListener("resize", updateThreshold);
};

// モバイル用ナビの開閉
const initMobileNav = () => {
  const navRoot = qs("[data-nav-root]");
  const navToggle = qs("[data-nav-toggle]");
  const navMenu = qs("[data-nav-menu]");
  if (!navRoot || !navToggle || !navMenu) return;

  const closeNav = () => {
    navRoot.classList.remove("is-open");
    navToggle.setAttribute("aria-expanded", "false");
    document.documentElement.style.removeProperty("overflow");
  };

  navToggle.addEventListener("click", () => {
    const isOpen = navRoot.classList.toggle("is-open");
    navToggle.setAttribute("aria-expanded", String(isOpen));
    if (isOpen) {
      document.documentElement.style.setProperty("overflow", "hidden", "important");
    } else {
      document.documentElement.style.removeProperty("overflow");
    }
  });

  onAll(qsa("a", navMenu), "click", closeNav);
};

// TOPの施設検索モーダル
const initHomeSearchModals = () => {
  const triggers = qsa("[data-home-search-modal-trigger]");
  const modals = qsa("[data-home-search-modal]");
  if (!triggers.length || !modals.length) return;

  let activeTrigger = null;

  const getTransitionDuration = (element) => {
    const styles = getComputedStyle(element);
    const durations = styles.transitionDuration.split(",");
    const delays = styles.transitionDelay.split(",");
    const parseTime = (value) => {
      const time = Number.parseFloat(value);
      if (Number.isNaN(time)) return 0;
      return value.trim().endsWith("ms") ? time : time * 1000;
    };

    return Math.max(
      0,
      ...durations.map((duration, index) => {
        const delay = delays[index % delays.length] || "0s";
        return parseTime(duration) + parseTime(delay);
      })
    );
  };

  const openModal = (trigger) => {
    const modalId = trigger.dataset.homeSearchModalTrigger;
    const modal = qs(`#home-search-modal-${modalId}`);
    if (!modal || modal.open) return;

    activeTrigger = trigger;
    delete modal.dataset.isClosing;
    modal.showModal();
    modal.getBoundingClientRect();
    modal.classList.add("is-visible");
    document.documentElement.style.setProperty("overflow", "hidden", "important");
    qs("[data-home-search-modal-close]", modal)?.focus();
  };

  const closeModal = (modal) => {
    if (!modal.open || modal.dataset.isClosing === "true") return;

    modal.dataset.isClosing = "true";
    modal.classList.remove("is-visible");

    let fallbackTimer;
    const finishClose = () => {
      window.clearTimeout(fallbackTimer);
      modal.removeEventListener("transitionend", finishClose);
      if (modal.open) {
        modal.close();
      }
    };
    const transitionDuration = getTransitionDuration(modal);

    if (!transitionDuration) {
      finishClose();
      return;
    }

    modal.addEventListener("transitionend", finishClose, { once: true });
    fallbackTimer = window.setTimeout(finishClose, transitionDuration + 50);
  };

  onAll(triggers, "click", (event) => openModal(event.currentTarget));
  onAll(triggers, "keydown", (event) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    openModal(event.currentTarget);
  });

  modals.forEach((modal) => {
    on(qs("[data-home-search-modal-close]", modal), "click", () => closeModal(modal));
    on(modal, "click", (event) => {
      if (event.target === modal) {
        closeModal(modal);
      }
    });
    on(modal, "cancel", (event) => {
      event.preventDefault();
      closeModal(modal);
    });
    on(modal, "close", () => {
      modal.classList.remove("is-visible");
      delete modal.dataset.isClosing;
      document.documentElement.style.removeProperty("overflow");
      activeTrigger?.focus();
      activeTrigger = null;
    });
  });
};

// TOPの施設検索タブ
const initHomeSearchTabs = () => {
  const tabList = qs("[data-home-search-tabs]");
  if (!tabList) return;

  const tabs = qsa("[data-home-search-tab]", tabList);
  const panels = qsa("[data-home-search-panel]");
  const tabTriggers = qsa("[data-home-search-tab-target]");
  if (!tabs.length || !panels.length) return;

  const activateTab = (activeTab, moveFocus = false) => {
    tabs.forEach((tab) => {
      const isActive = tab === activeTab;
      tab.classList.toggle("is-active", isActive);
      tab.setAttribute("aria-selected", String(isActive));
      tab.tabIndex = isActive ? 0 : -1;
    });

    panels.forEach((panel) => {
      const isActive = panel.id === activeTab.getAttribute("aria-controls");
      panel.classList.toggle("is-active", isActive);
      panel.hidden = !isActive;

      if (isActive) {
        requestAnimationFrame(() => {
          qsa("[data-keen-carouselCentered]", panel).forEach((slider) => {
            slider._keenCenteredInstance?.update();
          });
        });
      }
    });

    if (moveFocus) {
      activeTab.focus();
    }
  };

  onAll(tabs, "click", (event) => activateTab(event.currentTarget));
  onAll(tabTriggers, "click", (event) => {
    const targetTab = qs(`#${event.currentTarget.dataset.homeSearchTabTarget}`, tabList);
    if (targetTab) {
      activateTab(targetTab);
    }
  });
  onAll(tabs, "keydown", (event) => {
    const currentIndex = tabs.indexOf(event.currentTarget);
    let nextIndex = currentIndex;

    if (event.key === "ArrowRight") nextIndex = (currentIndex + 1) % tabs.length;
    if (event.key === "ArrowLeft") nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
    if (event.key === "Home") nextIndex = 0;
    if (event.key === "End") nextIndex = tabs.length - 1;
    if (nextIndex === currentIndex) return;

    event.preventDefault();
    activateTab(tabs[nextIndex], true);
  });

  activateTab(qs('[aria-selected="true"]', tabList) || tabs[0]);
};

const homeSectionNavConfigs = [
  {
    navSelector: "[data-accommodations-nav]",
    sectionSelector: "[data-accommodations-section]"
  },
  {
    navSelector: "[data-brand-nav]",
    sectionSelector: "[data-brand-section]"
  }
];

// ページ内リンクのスムーススクロール
const initSmoothAnchors = () => {
  const anchors = qsa('a[href^="#"]');
  const sectionNavs = homeSectionNavConfigs
    .map(({ navSelector, sectionSelector }) => ({
      nav: qs(navSelector),
      sectionSelector
    }))
    .filter(({ nav }) => nav);

  const resolveCssLength = (value) => {
    if (!value || !document.body) return 0;
    const probe = document.createElement("div");
    probe.style.position = "absolute";
    probe.style.visibility = "hidden";
    probe.style.height = value;
    document.body.appendChild(probe);
    const height = probe.getBoundingClientRect().height;
    probe.remove();
    return height;
  };

  onAll(anchors, "click", (event) => {
    const targetId = event.currentTarget.getAttribute("href");
    if (!targetId || targetId === "#") return;
    const targetEl = qs(targetId);
    if (!targetEl) return;
    event.preventDefault();

    const isHomeSectionNavigation = sectionNavs.some(
      ({ nav, sectionSelector }) =>
        nav.contains(event.currentTarget) && targetEl.matches(sectionSelector)
    );
    const isPcSideNavigation = Boolean(
      event.currentTarget.closest(".l-side__navigation")
    );

    const needsHeaderHeightChangeAdjustment =
      isHomeSectionNavigation || isPcSideNavigation || targetId === "#search";

    if (needsHeaderHeightChangeAdjustment) {
      const scrollMargin = Number.parseFloat(
        getComputedStyle(targetEl).scrollMarginBlockStart
      );
      const header = qs("[data-header]");
      const currentHeaderHeight = header
        ? header.getBoundingClientRect().height
        : 0;
      const scrolledHeaderHeight = resolveCssLength(
        getComputedStyle(document.documentElement)
          .getPropertyValue("--global-header-scrolled-height")
          .trim()
      );
      const headerHeightChange = Math.max(
        0,
        currentHeaderHeight - scrolledHeaderHeight
      );
      const overlap = targetId === "#search" ? 1 : 0;
      const targetTop = window.scrollY + targetEl.getBoundingClientRect().top;
      window.scrollTo({
        top: Math.max(
          0,
          targetTop -
            (Number.isNaN(scrollMargin) ? 0 : scrollMargin) -
            headerHeightChange +
            overlap
        ),
        behavior: "smooth"
      });
      return;
    }

    targetEl.scrollIntoView({ behavior: "smooth" });
  });
};

// TOPのカテゴリーナビとセクションのスクロール同期
const initHomeSectionNav = (navSelector, sectionSelector) => {
  const nav = qs(navSelector);
  if (!nav) return;
  const links = qsa('a[href^="#"]', nav);
  const sections = links
    .map((link) => qs(link.getAttribute("href")))
    .filter((section) => section?.matches(sectionSelector));

  if (!sections.length) return;

  const getHeaderHeight = () => {
    const header = qs("[data-header]");
    if (header) {
      return header.getBoundingClientRect().height;
    }
    const value = getComputedStyle(document.documentElement)
      .getPropertyValue("--global-header-scrolled-height")
      .trim();
    const parsed = parseFloat(value);
    return Number.isNaN(parsed) ? 0 : parsed;
  };

  const getNavHeight = () => {
    const renderedHeight = nav.getBoundingClientRect().height;
    if (renderedHeight) return renderedHeight;

    const parsed = parseFloat(getComputedStyle(nav).height);
    return Number.isNaN(parsed) ? 0 : parsed;
  };

  const setActive = (id) => {
    links.forEach((link) => {
      const targetId = link.getAttribute("href");
      link.classList.toggle("is-active", targetId === `#${id}`);
    });
  };

  const clearActive = () => {
    links.forEach((link) => link.classList.remove("is-active"));
  };

  const isAtPageTop = () => !window.location.hash && window.scrollY <= 16;

  const updateByHash = () => {
    const hash = window.location.hash.replace("#", "");
    if (!hash) {
      isCategoryNavigationPending = false;
      clearActive();
      return;
    }
    isCategoryNavigationPending = false;
    setActive(hash);
  };

  let observer;
  const sectionEntries = new Map();
  let isCategoryNavigationPending = false;
  let categoryNavigationScrollTimer;
  const scheduleCategoryNavigationEnd = () => {
    clearTimeout(categoryNavigationScrollTimer);
    categoryNavigationScrollTimer = setTimeout(() => {
      isCategoryNavigationPending = false;
    }, 100);
  };
  const createObserver = () => {
    if (observer) observer.disconnect();
    sectionEntries.clear();
    const topOffset = getHeaderHeight() + getNavHeight();
    observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => sectionEntries.set(entry.target, entry));

        // ページ内移動中は途中のセクションでクリック直後の選択状態を上書きさせない。
        if (isCategoryNavigationPending) return;

        if (isAtPageTop() && !isCategoryNavigationPending) {
          clearActive();
          return;
        }

        const visible = sections
          .map((section) => sectionEntries.get(section))
          .filter((entry) => entry?.isIntersecting);
        if (!visible.length) {
          const firstSection = sections[0];
          if (
            firstSection &&
            firstSection.getBoundingClientRect().top > topOffset &&
            !isCategoryNavigationPending
          ) {
            clearActive();
          }
          return;
        }
        visible.sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        setActive(visible[0].target.id);
      },
      {
        rootMargin: `-${topOffset}px 0px -60% 0px`,
        threshold: [0, 0.25, 0.5, 0.75, 1]
      }
    );
    sections.forEach((section) => observer.observe(section));
  };

  onAll(links, "click", (event) => {
    const targetId = event.currentTarget.getAttribute("href")?.replace("#", "");
    if (targetId) {
      isCategoryNavigationPending = true;
      setActive(targetId);
      scheduleCategoryNavigationEnd();
    }
  });

  window.addEventListener("hashchange", updateByHash);
  window.addEventListener("scroll", () => {
    if (isCategoryNavigationPending) {
      scheduleCategoryNavigationEnd();
      return;
    }

    if (isAtPageTop()) {
      clearActive();
    }
  }, { passive: true });
  window.addEventListener("resize", createObserver);
  createObserver();
  updateByHash();
};

const initHomeSectionNavs = () => {
  homeSectionNavConfigs.forEach(({ navSelector, sectionSelector }) => {
    initHomeSectionNav(navSelector, sectionSelector);
  });
};

// 下層ページのアコーディオン
const initAccessAccordion = () => {
  const items = qsa("[data-accordion-item]");
  if (!items.length) return;

  const resizeHandlers = [];

  items.forEach((item) => {
    const summary = qs("[data-accordion-summary]", item);
    const content = qs("[data-accordion-content]", item);
    if (!summary || !content) return;

    const inner = qs("[data-accordion-inner]", content);
    if (!inner) return;
    let animation = null;

    const setOpenState = (open) => {
      item.classList.toggle("is-open", open);
      item.open = open;
      content.style.opacity = open ? "1" : "0";
      content.style.height = open ? "auto" : "0px";
    };

    setOpenState(item.open);

    const handleResize = () => {
      if (!item.classList.contains("is-open")) return;
      if (animation) {
        animation.cancel();
        animation = null;
      }
      content.style.height = "auto";
    };

    resizeHandlers.push(handleResize);

    summary.addEventListener("click", (event) => {
      event.preventDefault();

      const isOpen = item.classList.contains("is-open");
      const startHeight = content.getBoundingClientRect().height;
      const endHeight = isOpen ? 0 : inner.scrollHeight;

      if (animation) {
        animation.cancel();
      }

      if (!isOpen) {
        item.open = true;
        item.classList.add("is-open");
      }

      content.style.height = `${startHeight}px`;
      content.style.opacity = isOpen ? "1" : "0";

      animation = content.animate(
        [
          { height: `${startHeight}px`, opacity: isOpen ? 1 : 0 },
          { height: `${endHeight}px`, opacity: isOpen ? 0 : 1 }
        ],
        {
          duration: 300,
          easing: "ease",
          fill: "forwards"
        }
      );

      animation.onfinish = () => {
        if (animation) {
          animation.oncancel = null;
          animation.cancel();
        }
        animation = null;
        setOpenState(!isOpen);
      };

      animation.oncancel = () => {
        animation = null;
      };
    });
  });

  window.addEventListener("resize", () => {
    resizeHandlers.forEach((handler) => handler());
  });
};

// 各機能の初期化をまとめて実行
const initAll = () => {
  initYouTubeHero();
  initHeroSlider();
  initCarouselCentered();
  initCarousel();
  initHeaderNavCarouselCentered();
  initHeaderScroll();
  initMobileNav();
  initHomeSearchModals();
  initHomeSearchTabs();
  initSmoothAnchors();
  initHomeSectionNavs();
  initAccessAccordion();
};

initAll();
